import { useState, useEffect } from 'react'
import { notificationsApi, type NotificationSettings, type PushToken } from '../../api/notifications'
import { FCM_TOKEN_ID_KEY } from '../../hooks/usePushNotifications'
import { useUiStore } from '../../stores/uiStore'

interface Props {
  onBack: () => void
}

function Toggle({ checked, onChange, label, description, disabled }: {
  checked: boolean
  onChange: (v: boolean) => void
  label: string
  description?: string
  disabled?: boolean
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-3">
      <div className="flex-1 min-w-0">
        <p className="text-txt-primary text-sm">{label}</p>
        {description && <p className="text-txt-meta text-xs mt-0.5">{description}</p>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent
          transition-colors duration-200 focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed
          ${checked ? 'bg-accent' : 'bg-txt-meta/30'}`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform duration-200
            ${checked ? 'translate-x-5' : 'translate-x-0'}`}
        />
      </button>
    </div>
  )
}

export function NotificationSettingsPanel({ onBack }: Props) {
  const [settings, setSettings] = useState<NotificationSettings>({
    sound: true,
    vibration: true,
    preview: true,
    dnd_enabled: false,
    dnd_start: '22:00',
    dnd_end: '08:00',
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [tokens, setTokens] = useState<PushToken[]>([])
  const [tokenSaving, setTokenSaving] = useState<Set<string>>(new Set())
  const [testing, setTesting] = useState(false)
  const { addToast } = useUiStore()
  const currentTokenId = localStorage.getItem(FCM_TOKEN_ID_KEY)

  useEffect(() => {
    Promise.all([
      notificationsApi.getSettings(),
      notificationsApi.listTokens().catch(() => ({ tokens: [] as PushToken[] })),
    ])
      .then(([s, t]) => { setSettings(s); setTokens(t.tokens ?? []) })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const handleTestPush = async () => {
    setTesting(true)
    try {
      const { results } = await notificationsApi.testPush()
      if (results.length === 0) {
        addToast('error', 'Нет зарегистрированных устройств для пуша')
      } else {
        const ok = results.filter((r) => r.ok).length
        const errs = results.filter((r) => !r.ok).map((r) => r.error).filter(Boolean)
        addToast(
          ok > 0 ? 'success' : 'error',
          `Отправлено ${ok}/${results.length}.${errs.length ? ' Ошибки: ' + errs.join('; ').slice(0, 140) : ' Если уведомление не пришло — проблема на стороне устройства/браузера.'}`,
        )
      }
    } catch {
      addToast('error', 'Не удалось отправить тестовый пуш')
    } finally {
      setTesting(false)
    }
  }

  const handleRemoveToken = async (tokenId: string) => {
    try {
      await notificationsApi.removeToken(tokenId)
      setTokens((prev) => prev.filter((t) => t.id !== tokenId))
      addToast('success', 'Устройство удалено')
    } catch {
      addToast('error', 'Не удалось удалить устройство')
    }
  }

  const handleTokenSetting = async (tokenId: string, field: 'notifications_enabled' | 'sound_enabled', value: boolean) => {
    setTokens((prev) =>
      prev.map((t) => (t.id === tokenId ? { ...t, [field]: value } : t))
    )
    setTokenSaving((s) => new Set(s).add(tokenId))
    const token = tokens.find((t) => t.id === tokenId)
    if (!token) return
    const next = {
      notifications_enabled: field === 'notifications_enabled' ? value : token.notifications_enabled,
      sound_enabled: field === 'sound_enabled' ? value : token.sound_enabled,
    }
    try {
      await notificationsApi.updateTokenSettings(tokenId, next)
    } catch {
      // Revert on error
      setTokens((prev) => prev.map((t) => (t.id === tokenId ? token : t)))
      addToast('error', 'Не удалось сохранить настройки устройства')
    } finally {
      setTokenSaving((s) => { const n = new Set(s); n.delete(tokenId); return n })
    }
  }

  const update = async (patch: Partial<NotificationSettings>) => {
    const next = { ...settings, ...patch }
    setSettings(next)
    setSaving(true)
    try {
      await notificationsApi.updateSettings(next)
    } catch {
      addToast('error', 'Не удалось сохранить настройки уведомлений')
      setSettings(settings)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col h-full bg-bg-primary">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-divider shrink-0">
        <button onClick={onBack} className="text-txt-secondary hover:text-txt-primary transition-colors">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
        </button>
        <h2 className="text-txt-primary font-semibold text-base flex-1">Уведомления</h2>
        {saving && <div className="w-4 h-4 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />}
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="w-5 h-5 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto px-4 py-2">
          <div className="bg-bg-input rounded-2xl px-4 divide-y divide-divider">
            <Toggle
              checked={settings.sound}
              onChange={(v) => update({ sound: v })}
              label="Звук"
              description="Воспроизводить звук при входящих сообщениях"
            />
            <Toggle
              checked={settings.vibration}
              onChange={(v) => update({ vibration: v })}
              label="Вибрация"
              description="Вибрация на мобильных устройствах"
            />
            <Toggle
              checked={settings.preview}
              onChange={(v) => update({ preview: v })}
              label="Предпросмотр сообщений"
              description="Показывать текст сообщения в уведомлениях"
            />
          </div>

          <p className="text-txt-meta text-xs uppercase tracking-wider font-medium mt-5 mb-2">Режим тишины</p>
          <div className="bg-bg-input rounded-2xl px-4 divide-y divide-divider">
            <Toggle
              checked={settings.dnd_enabled}
              onChange={(v) => update({ dnd_enabled: v })}
              label="Режим тишины"
              description="Отключать уведомления в заданные часы"
            />
            {settings.dnd_enabled && (
              <div className="py-3 flex items-center gap-3">
                <div className="flex-1">
                  <p className="text-txt-meta text-xs mb-1">С</p>
                  <input
                    type="time"
                    value={settings.dnd_start}
                    onChange={(e) => update({ dnd_start: e.target.value })}
                    className="bg-bg-primary text-txt-primary text-sm rounded-xl px-3 py-1.5 outline-none focus:ring-1 focus:ring-accent"
                  />
                </div>
                <div className="flex-1">
                  <p className="text-txt-meta text-xs mb-1">До</p>
                  <input
                    type="time"
                    value={settings.dnd_end}
                    onChange={(e) => update({ dnd_end: e.target.value })}
                    className="bg-bg-primary text-txt-primary text-sm rounded-xl px-3 py-1.5 outline-none focus:ring-1 focus:ring-accent"
                  />
                </div>
              </div>
            )}
          </div>

          {tokens.length > 0 && (
            <>
              <p className="text-txt-meta text-xs uppercase tracking-wider font-medium mt-5 mb-2">Подключённые устройства</p>
              <div className="bg-bg-input rounded-2xl divide-y divide-divider">
                {tokens.map((token) => {
                  const isCurrent = token.id === currentTokenId
                  const isBusy = tokenSaving.has(token.id)
                  return (
                    <div key={token.id} className="px-4 py-3">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-txt-primary text-sm truncate">{token.device_name || 'Неизвестное устройство'}</p>
                            {isCurrent && (
                              <span className="text-[10px] bg-accent/15 text-accent px-1.5 py-0.5 rounded-full shrink-0">Это устройство</span>
                            )}
                          </div>
                          <p className="text-txt-meta text-xs mt-0.5">{token.platform} · {new Date(token.created_at).toLocaleDateString()}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {isBusy && <div className="w-3 h-3 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />}
                          <button
                            onClick={() => handleRemoveToken(token.id)}
                            className="text-red-400 hover:text-red-300 text-xs transition-colors"
                          >
                            Удалить
                          </button>
                        </div>
                      </div>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={token.notifications_enabled}
                            disabled={isBusy}
                            onChange={(e) => handleTokenSetting(token.id, 'notifications_enabled', e.target.checked)}
                            className="w-3.5 h-3.5 accent-accent"
                          />
                          <span className="text-txt-secondary text-xs">Уведомления</span>
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={token.sound_enabled}
                            disabled={isBusy || !token.notifications_enabled}
                            onChange={(e) => handleTokenSetting(token.id, 'sound_enabled', e.target.checked)}
                            className="w-3.5 h-3.5 accent-accent"
                          />
                          <span className="text-txt-secondary text-xs">Звук</span>
                        </label>
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
          )}

          {/* Тест доставки уведомлений */}
          <p className="text-txt-meta text-xs uppercase tracking-wider font-medium mt-5 mb-2">Диагностика</p>
          <button
            onClick={handleTestPush}
            disabled={testing}
            className="w-full bg-bg-input hover:bg-bg-hover disabled:opacity-50 text-txt-primary text-sm rounded-xl py-2.5 transition-colors"
          >
            {testing ? 'Отправка...' : '🔔 Отправить тестовый пуш'}
          </button>
          <p className="text-txt-meta text-xs mt-2">
            Отправит уведомление на это и другие ваши устройства. Если не пришло — покажет причину.
          </p>
        </div>
      )}
    </div>
  )
}
